import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { acceso360Client } from '@/lib/axios';
import { IamBackend } from '@/lib/BackendUrls';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { UUID } from '@/types';
import { parseError } from '@/utils/axios';
import { formatString } from '@/utils/format';
import { emptyStringValues } from '@/utils/objects';

import { User, UserStateEnum } from '../types/types';

import { userQueryKeys } from './query-keys';

export type UpdateUserDataDTO = {
  phone_number?: string;
  phone_prefix?: string;
};

export interface UpdateUserDTO {
  id: UUID;
  first_name?: string;
  last_name?: string | null;
  username?: string;
  state?: UserStateEnum;
  data?: UpdateUserDataDTO;
}

export async function updateUser({ id, ...data }: UpdateUserDTO): Promise<User> {
  return acceso360Client
    .patch(formatString(IamBackend.users.one.get, id), emptyStringValues(data))
    .then(({ data }) => data);
}

type MutationFn = typeof updateUser;

interface UseUpdateUserParams {
  hideNotifications?: boolean;
  config?: MutationConfig<MutationFn>;
}

export function useUpdateUser({ config, hideNotifications = false }: UseUpdateUserParams = {}) {
  const { t } = useTranslation();

  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    onError(error) {
      const { status, fields } = parseError(error);

      if (status === 404) {
        addErrorNotification(t('El usuario no existe'));
      } else if (status === 409) {
        if (fields.includes('email')) {
          addErrorNotification(t('El correo electrónico ya existe'));
        } else if (fields.includes('username')) {
          addErrorNotification(t('El nombre de usuario ya existe'));
        } else {
          addErrorNotification(
            t('No se ha podido actualizar porque existe un conflicto con otro usuario')
          );
        }
      } else {
        addErrorNotification(t('No se ha podido actualizar el usuario'));
        console.error(error);
      }
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
      if (!hideNotifications) addSuccessNotification(t('El usuario se ha actualizado'));
    },
    ...config,
    mutationFn: updateUser,
  });
}
